import { z } from 'zod'

import { slugSchema } from '@scalar-org/entities/utility'

// ---------------------------------------------------------------------------
// Icons

// Icon schema that accepts predefined icons, URLs, or local file paths
const iconSchema = z.union([
  z
    .string()
    .meta({ schemaRef: 'https://cdn.scalar.com/schema/icon-enum.json' })
    .describe(
      "Predefined icon from the Scalar icon library (e.g., 'line/arrow-chevron-down', 'solid/interface-add', 'brand/programming-language-typescript')",
    ),
  z
    .string()
    .url()
    .describe(
      "URL to a remote icon file (e.g., 'https://example.com/icon.svg')",
    ),
  z
    .string()
    .describe(
      "Local file path relative to the project root (e.g., './assets/custom-icon.svg', '/images/logo.png')",
    ),
])

// ---------------------------------------------------------------------------
// Sidebar items from a github sync config

export type ConfigSidebarPage = {
  path: string
  name?: string
  sidebarTitle?: string // Optional title for the sidebar entry
  description?: string
  backgroundImage?: string
  icon?: string // Can be predefined icon, URL, or file path
  type: 'page'
  defaultOpen?: boolean
  children?: ConfigSidebarItem[]
}

const sidebarPageSchema = z.object({
  name: z.string().optional().default(''),
  sidebarTitle: z
    .string()
    .optional()
    .describe(
      'Optional title for the sidebar entry. If not provided, defaults to the name, first H1 found, or path.',
    ),
  description: z.string().optional().default(''),
  backgroundImage: z.string().optional(),
  icon: iconSchema.optional(),
  path: z.string(),
  type: z.literal('page'),
  defaultOpen: z
    .boolean()
    .optional()
    .describe('Expand page items in sidebar by default'),
  children: z.lazy(() => sidebarItemSchema.array()).optional(),
}) satisfies z.ZodType<ConfigSidebarPage>

export type ConfigSidebarFolder = {
  name: string
  type: 'folder'
  children: ConfigSidebarItem[]
  icon?: string // Can be predefined icon, URL, or file path
  defaultOpen?: boolean
}

const sidebarFolderSchema = z.object({
  name: z.string(),
  icon: iconSchema.optional(),
  type: z.literal('folder'),
  defaultOpen: z
    .boolean()
    .optional()
    .describe('Expand folder items in sidebar by default'),
  children: z.lazy(() => sidebarItemSchema.array()),
}) satisfies z.ZodType<ConfigSidebarFolder>

export type ConfigSidebarLink = {
  name: string
  type: 'link'
  url: string
  icon?: string // Can be predefined icon, URL, or file path
}

const sidebarLinkSchema = z.object({
  name: z.string(),
  icon: iconSchema.optional(),
  type: z.literal('link'),
  url: z.string(),
}) satisfies z.ZodType<ConfigSidebarLink>

export type ConfigSidebarItem =
  | ConfigSidebarPage
  | ConfigSidebarFolder
  | ConfigSidebarLink

const sidebarItemSchema: z.ZodType<ConfigSidebarItem> = z.union([
  sidebarLinkSchema,
  sidebarPageSchema,
  sidebarFolderSchema,
])

// ---------------------------------------------------------------------------
// Guide/references configs

/**
 * Auto generated guide based on the repository folder structure
 * Links are not supported at this time
 */
export type GuideDirectory = {
  name: string
  slug?: string
  description?: string
  /** Root directory for markdown files. Subfolder content will be added to the guide  */
  folder: string
  /** Optional glob patterns for inclusion  */
  include?: string[]
  /** Option glob patterns for exclusion */
  exclude?: string[]
}

/**
 * Explicit specification for the guide sidebar
 * Sidebar entries can include:
 * - markdown files (1:1 with a page)
 * - sidebar folders (has child pages, folders, and links)
 * - links (external links)
 */
export type GuideExplicit = {
  name: string
  slug?: string
  description?: string
  sidebar: ConfigSidebarItem[]
}

export type ConfigGuide = GuideDirectory | GuideExplicit

const configGuideSchema = z.union([
  z.object({
    name: z.string(),
    slug: z.string().optional(),
    folder: z.string(),
    include: z.string().array().optional(),
    exclude: z.string().array().optional(),
    description: z.string().default(''),
  }),
  z.object({
    name: z.string(),
    slug: z.string().optional(),
    sidebar: sidebarItemSchema.array(),
    description: z.string().default(''),
  }),
]) satisfies z.ZodType<ConfigGuide>

const configReferenceSchemaBase = z.object({
  name: z.string(),
  slug: z.string().optional(),
  description: z.string().default(''),
})

const configReferenceSchema = z.union([
  configReferenceSchemaBase.extend({
    liveLink: z.string(),
  }),
  configReferenceSchemaBase.extend({
    slug: z.string(),
    namespace: z.string(),
    versions: z.string().array().default(['latest']),
  }),
  configReferenceSchemaBase.extend({
    path: z.string(),
  }),
])

export type ConfigReference = z.infer<typeof configReferenceSchema>

const optionalUrl = z.url().optional()

// ---------------------------------------------------------------------------
// Header Schema

const configHeaderGuideSchema = z.object({
  type: z.literal('guide'),
  slug: z.string(),
})

export type ConfigHeaderGuide = z.infer<typeof configHeaderGuideSchema>

const configHeaderReferenceSchema = z.object({
  type: z.literal('reference'),
  slug: z.string(),
})

export type ConfigHeaderReference = z.infer<typeof configHeaderReferenceSchema>

const configHeaderExternalLinkSchema = z.object({
  type: z.literal('link'),
  title: z.string(),
  link: z.string(),
  isButton: z.boolean().optional().default(false),
})

export type ConfigHeaderExternalLink = z.infer<
  typeof configHeaderExternalLinkSchema
>

const configHeaderGroupSchema = z.object({
  type: z.literal('group'),
  title: z.string(),
  children: z
    .array(
      z.union([
        configHeaderGuideSchema,
        configHeaderReferenceSchema,
        configHeaderExternalLinkSchema,
      ]),
    )
    .describe(
      'Children of the group. Groups cannot contain other groups, spacers, or the version selector',
    ),
})

export type ConfigHeaderGroup = z.infer<typeof configHeaderGroupSchema>

const configHeaderSpacerSchema = z.object({
  type: z.literal('spacer'),
})

export type ConfigHeaderSpacer = z.infer<typeof configHeaderSpacerSchema>

const configHeaderVersionSelectorSchema = z.object({
  type: z.literal('version-selector'),
})

export type ConfigHeaderVersionSelector = z.infer<
  typeof configHeaderVersionSelectorSchema
>

const configHeaderSchema = z.union([
  configHeaderGuideSchema,
  configHeaderReferenceSchema,
  configHeaderExternalLinkSchema,
  configHeaderGroupSchema,
  configHeaderSpacerSchema,
  configHeaderVersionSelectorSchema,
])

export type ConfigHeader = z.infer<typeof configHeaderSchema>

// ---------------------------------------------------------------------------
// Config Schema

export const scalarConfigSchemaLegacy = z.object({
  $schema: z
    .string()
    .url()
    .default('https://cdn.scalar.com/schema/scalar-config.json'),
  /** Optional root directory specification relative to the config file. */
  root: z.string().optional(),
  publishOnMerge: z.boolean().optional(),
  insertPageTitles: z
    .boolean()
    .default(true)
    .describe(
      'Whether to insert an H1 with Title and Description at top of each guide page, or not. Deprecated. Write the title and description in the markdown instead.',
    ),
  subdomain: z.preprocess((arg) => {
    // Only take the subdomain if the full domain is provided
    return typeof arg === 'string' ? arg.split('.')[0] : arg
  }, z.string().min(3).max(30).optional()),
  customDomain: z.string().min(3).optional(),
  loginPortal: slugSchema.optional(),
  isPrivate: z.boolean().optional(),
  assetsDir: z.string().optional(),
  accessGroups: slugSchema.array().optional(),
  siteMeta: z
    .object({
      favicon: optionalUrl,
      ogImage: optionalUrl,
      title: z.string().optional(),
      description: z.string().optional(),
    })
    .default({}),
  siteConfig: z
    .object({
      theme: z.string().optional(),
      logo: z
        .union([
          optionalUrl,
          z.object({ darkMode: z.url(), lightMode: z.url() }),
        ])
        .optional(),
      footer: z.string().optional(),
      footerCss: z.string().optional(),
      footerBelowSidebar: z.boolean().optional(),
      headScript: z.string().optional(),
      bodyScript: z.string().optional(),
      cssFiles: z.string().array().optional(),
      cssString: z.string().optional(),
      routing: z
        .object({
          guidePathPattern: z.string().optional(),
          referencePathPattern: z.string().optional(),
          redirects: z
            .object({
              from: z.string(),
              to: z.string(),
            })
            .array()
            .default([]),
        })
        .optional(),
    })
    .default({}),
  guides: configGuideSchema.array().optional().default([]),
  references: configReferenceSchema.array().optional().default([]),
  header: configHeaderSchema.array().optional().default([]),
  /**
   * MDX component directory where elements will get automatically injected into
   * MDX files without explicit imports
   */
  snippets: z.string().optional().default('snippets'),
})

/** Top Level Scalar Config Object */
export type ScalarConfigLegacy = z.infer<typeof scalarConfigSchemaLegacy>
